import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Link2, Loader2, ExternalLink, ShieldCheck, X, Rocket, Sparkles,
  RefreshCw, AlertCircle, Store, Camera,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { etsyListingAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { computePrice, metalsForProductType, caratsForProductType } from './EtsyPricingTable';
import { isTennis as isTennisType, IGI_UPCHARGE, tennisPriceRange } from '../lib/tennis';

const ETSY_FEE = 0.06;
const DEFAULT_MATERIALS = ['Diamond', 'White gold', 'Yellow gold', 'Rose gold', 'Solid gold', 'Platinum', 'Silver'];
const DEFAULT_ENGRAVING = "Complimentary engraving — tell us the text you'd like engraved, or write 'no engraving'.";
const DEFAULT_RING_SIZE = "Please share your ring size (US sizing). Not sure? Message us and we'll send a free ring sizer before we begin crafting.";
const RING_PRODUCT_TYPES = new Set(['engagement_ring', 'engagement_ring_set', 'wedding_band']);

const fmt = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

/**
 * Etsy Draft Publisher — connect the brand's Etsy shop via OAuth, then push
 * the product (title + description + tags + images + full Metal × Carat
 * variation matrix with sale markup) as a DRAFT listing.
 */
export default function EtsyListingPanel({ product, onUpdate }) {
  const { isEbayUser } = useAuth(); // same marketplace allow-list as eBay
  const allowed = isEbayUser?.();

  const [status, setStatus] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  // Push settings
  const [shippingProfileId, setShippingProfileId] = useState('');
  const [readinessStateId, setReadinessStateId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [taxonomyId, setTaxonomyId] = useState('');
  const [taxonomyQuery, setTaxonomyQuery] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [materials, setMaterials] = useState(DEFAULT_MATERIALS);
  const [markupPct, setMarkupPct] = useState(20);
  const [quantity, setQuantity] = useState(1);
  const [personalization, setPersonalization] = useState(true);
  const [persInstructions, setPersInstructions] = useState(DEFAULT_ENGRAVING);
  const [ringSize, setRingSize] = useState(true);
  const [ringSizeInstructions, setRingSizeInstructions] = useState(DEFAULT_RING_SIZE);
  const [includeVideo, setIncludeVideo] = useState(true);

  const [generatingTags, setGeneratingTags] = useState(false);
  const [attrDisplay, setAttrDisplay] = useState(null);
  const [derivedSel, setDerivedSel] = useState({ cut_type: [], setting: [], shank_type: [], style: [], materials: [] });
  const [pushing, setPushing] = useState(false);
  const [job, setJob] = useState(null);
  const [jobActive, setJobActive] = useState(false);

  const etsyListing = product?.etsy_listing;
  const productType = product?.product_type || 'engagement_ring';
  const isTennis = isTennisType(productType);
  const photos = (product?.media || []).filter((m) => m.r2_key && (!m.kind || m.kind === 'photo' || m.kind === 'image'));
  const hasVideo = (product?.media || []).some((m) => m.r2_key && m.kind === 'video');

  // ---------- connection ----------
  const loadStatus = useCallback(async () => {
    if (!allowed) return;
    try {
      const res = await etsyListingAPI.oauthStatus();
      setStatus(res.data);
    } catch (err) {
      setStatus({ connected: false, error: err.response?.data?.detail || 'Status check failed' });
    }
  }, [allowed]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (!allowed) return undefined;
    const onMsg = (e) => {
      const data = e?.data;
      if (data && data.type === 'etsy-oauth') {
        if (data.success) {
          toast.success(data.message || 'Etsy connected');
          loadStatus();
        } else {
          toast.error(data.message || 'Etsy connection failed');
        }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [allowed, loadStatus]);

  const handleConnect = async () => {
    try {
      const res = await etsyListingAPI.oauthStart();
      const w = 620, h = 760;
      const left = (window.screen.width - w) / 2;
      const top = (window.screen.height - h) / 2;
      window.open(res.data.auth_url, 'etsy-oauth', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Etsy OAuth');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Etsy? You will need to re-authorize to push new drafts.')) return;
    try {
      await etsyListingAPI.oauthDisconnect();
      setStatus({ connected: false });
      setDefaults(null);
      toast.success('Etsy disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    }
  };

  // ---------- shop defaults ----------
  useEffect(() => {
    if (!status?.connected || defaults || loadingDefaults) return;
    setLoadingDefaults(true);
    etsyListingAPI.shopDefaults()
      .then((res) => setDefaults(res.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load Etsy shop settings'))
      .finally(() => setLoadingDefaults(false));
  }, [status?.connected, defaults, loadingDefaults]);

  // Prefill from saved settings / suggestions once defaults arrive
  useEffect(() => {
    if (!defaults) return;
    const s = product?.etsy_push_settings || {};
    setShippingProfileId(String(s.shipping_profile_id || defaults.shipping_profiles?.[0]?.id || ''));
    setReadinessStateId(String(s.readiness_state_id || defaults.readiness_states?.[0]?.id || ''));
    setSectionId(String(s.shop_section_id || ''));
    const suggested = defaults.suggested_taxonomy?.[productType];
    setTaxonomyId(String(s.taxonomy_id || suggested?.id || ''));
    if (s.markup_pct !== undefined) setMarkupPct(s.markup_pct);
    if (s.quantity !== undefined) setQuantity(s.quantity);
    // Engraving (formerly "personalization")
    const engravingOn = s.engraving ?? s.personalization;
    if (engravingOn !== undefined) setPersonalization(!!engravingOn);
    const engravingInstr = s.engraving_instructions || s.personalization_instructions;
    if (engravingInstr) setPersInstructions(engravingInstr);
    // Ring Size — default ON for ring product types
    if (s.ring_size !== undefined) setRingSize(!!s.ring_size);
    else setRingSize(RING_PRODUCT_TYPES.has(productType));
    if (s.ring_size_instructions) setRingSizeInstructions(s.ring_size_instructions);
  }, [defaults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill tags/materials from the product doc
  useEffect(() => {
    if (product?.etsy_tags?.length) setTags(product.etsy_tags);
    if (product?.etsy_materials?.length) setMaterials(product.etsy_materials);
  }, [product?.etsy_tags, product?.etsy_materials]);

  // Load structured attributes (fixed + derived + allowed values) for the editor
  useEffect(() => {
    if (!allowed || !status?.connected || !product?.id) return;
    etsyListingAPI.getAttributes(product.id)
      .then((res) => {
        if (res.data?.display) setAttrDisplay(res.data.display);
        if (res.data?.selection) setDerivedSel((prev) => ({ ...prev, ...res.data.selection }));
      })
      .catch(() => {});
  }, [allowed, status?.connected, product?.id]);

  const toggleMulti = (key, val, max) => {
    setDerivedSel((prev) => {
      const cur = prev[key] || [];
      if (cur.includes(val)) return { ...prev, [key]: cur.filter((x) => x !== val) };
      if (cur.length >= max) { toast.error(`Etsy allows max ${max} for this attribute`); return prev; }
      return { ...prev, [key]: [...cur, val] };
    });
  };

  // ---------- job polling ----------
  useEffect(() => {
    if (!allowed || !product?.id) return;
    etsyListingAPI.pushStatus(product.id).then((res) => {
      const j = res.data.job;
      if (j && j.status === 'pending') { setJob(j); setJobActive(true); }
    }).catch(() => {});
  }, [allowed, product?.id]);

  useEffect(() => {
    if (!jobActive || !product?.id) return undefined;
    const t = setInterval(async () => {
      try {
        const res = await etsyListingAPI.pushStatus(product.id);
        const j = res.data.job;
        setJob(j);
        if (j && j.status !== 'pending') {
          setJobActive(false);
          if (j.status === 'done') {
            toast.success(`Etsy draft ${j.mode === 'update' ? 'updated' : 'created'} · listing ${j.listing_id}`);
            if (onUpdate && res.data.etsy_listing) {
              onUpdate({ ...product, etsy_listing: res.data.etsy_listing, etsy_push_settings: res.data.etsy_push_settings });
            }
          } else {
            toast.error(j.error || 'Etsy push failed');
          }
        }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [jobActive, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- tags ----------
  const handleGenerateTags = async () => {
    try {
      setGeneratingTags(true);
      const res = await etsyListingAPI.generateTags(product.id);
      setTags(res.data.tags || []);
      if (res.data.materials?.length) setMaterials(res.data.materials);
      if (res.data.attributes_display) setAttrDisplay(res.data.attributes_display);
      if (res.data.attributes) setDerivedSel((prev) => ({ ...prev, ...res.data.attributes }));
      toast.success(`Generated ${res.data.tags?.length || 0} tags + Etsy attributes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tag generation failed');
    } finally {
      setGeneratingTags(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/[^a-z0-9 ]/g, '').slice(0, 20).trim();
    if (!t) return;
    if (tags.includes(t)) { setTagInput(''); return; }
    if (tags.length >= 13) { toast.error('Etsy allows max 13 tags'); return; }
    setTags([...tags, t]);
    setTagInput('');
  };

  // ---------- price preview ----------
  const preview = useMemo(() => {
    if (!product) return null;
    if (isTennis) {
      const r = tennisPriceRange(product.tennis_prices, productType);
      return r && { baseMin: r.min, baseMax: r.max, listMin: r.min, listMax: r.max, variantCount: r.count, metalCount: 2 };
    }
    const sc = Number(product.setting_stone_carat_weight || 0);
    const bc = Number(product.band_setting_stone_carat_weight || 0);
    const isBand = productType === 'wedding_band';
    const totals = [];
    metalsForProductType(productType).forEach((m) => {
      // Wedding bands have no carat dimension — one row per metal.
      const carats = isBand ? [0] : caratsForProductType(productType);
      carats.forEach((c) => {
        totals.push(computePrice(m.metalCost, c, sc, ETSY_FEE, {
          productType,
          isSpecialtyCut: !!product.is_specialty_cut,
          bandSettingStoneCarat: bc,
          metalKey: m.key,
        }).total);
      });
    });
    const d = Math.max(0, Math.min(Number(markupPct) || 0, 80)) / 100;
    const min = Math.min(...totals), max = Math.max(...totals);
    const metalCount = metalsForProductType(productType).length;
    const variantCount = isBand
      ? 11               // 11 metal/color combos, no carat dimension
      : 11 * caratsForProductType(productType).length;
    return {
      baseMin: min, baseMax: max,
      listMin: min / (1 - d), listMax: max / (1 - d),
      variantCount, metalCount,
    };
  }, [product, markupPct, productType, isTennis]);

  // ---------- push ----------
  const handlePush = async () => {
    if (!taxonomyId) { toast.error('Pick an Etsy category first'); return; }
    if (!shippingProfileId) { toast.error('Pick a shipping profile first'); return; }
    if (!photos.length && !etsyListing?.listing_id) { toast.error('Upload at least one photo first'); return; }
    if (isTennis && !preview) { toast.error('Save the tennis prices in the pricing panel first'); return; }
    try {
      setPushing(true);
      const res = await etsyListingAPI.pushDraft(product.id, {
        taxonomy_id: Number(taxonomyId),
        shipping_profile_id: Number(shippingProfileId),
        readiness_state_id: readinessStateId ? Number(readinessStateId) : null,
        shop_section_id: sectionId ? Number(sectionId) : null,
        tags,
        materials,
        attributes: derivedSel,
        markup_pct: isTennis ? 0 : Number(markupPct) || 0,
        quantity: Number(quantity) || 1,
        engraving: isTennis ? false : personalization,
        engraving_instructions: persInstructions,
        ring_size: isTennis ? false : ringSize,
        ring_size_instructions: ringSizeInstructions,
        include_video: includeVideo,
      });
      setJob({ status: 'pending', step: 'Queued', id: res.data.job_id });
      setJobActive(true);
      if (res.data.already_running) toast.info('A push is already running for this product');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push failed to start');
    } finally {
      setPushing(false);
    }
  };

  // ---------- push pricing only (refresh inventory matrix on existing draft) ----------
  const [pushingPricing, setPushingPricing] = useState(false);
  const handlePushPricingOnly = async () => {
    if (!etsyListing?.listing_id) {
      toast.error('No Etsy draft to refresh — push the full draft first');
      return;
    }
    if (!window.confirm(
      `Refresh the price matrix on Etsy draft ${etsyListing.listing_id}? Title, description, images and personalization will NOT change.`,
    )) return;
    try {
      setPushingPricing(true);
      const res = await etsyListingAPI.pushPricing(product.id, {
        markup_pct: isTennis ? 0 : Number(markupPct) || 0,
        quantity: Number(quantity) || 1,
        readiness_state_id: readinessStateId ? Number(readinessStateId) : null,
      });
      setJob({ status: 'pending', step: 'Refreshing prices', id: res.data.job_id, mode: 'pricing' });
      setJobActive(true);
      if (res.data.already_running) toast.info('A push is already running for this product');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pricing push failed');
    } finally {
      setPushingPricing(false);
    }
  };

  // ---------- push media only (replace every image on the listing) ----------
  const [pushingMedia, setPushingMedia] = useState(false);
  const [mediaIncludeVideo, setMediaIncludeVideo] = useState(false);
  const handlePushMediaOnly = async () => {
    if (!etsyListing?.listing_id) {
      toast.error('No Etsy listing linked — link one first, or push a draft');
      return;
    }
    const photoCount = (product?.media || []).filter((m) => m.r2_key && (m.kind === undefined || m.kind === 'photo' || m.kind === 'image')).length;
    if (!photoCount) {
      toast.error('Upload at least one photo before pushing media');
      return;
    }
    if (!window.confirm(
      `Replace all images on Etsy listing ${etsyListing.listing_id} with the ${photoCount} photo${photoCount === 1 ? '' : 's'} from this product?\n\nExisting Etsy photos will be DELETED first. Title, description, price and tags are unchanged.${mediaIncludeVideo ? '\n\nVideo will also be re-uploaded.' : ''}`,
    )) return;
    try {
      setPushingMedia(true);
      const res = await etsyListingAPI.pushMedia(product.id, { include_video: mediaIncludeVideo });
      setJob({ status: 'pending', step: 'Replacing media', id: res.data.job_id, mode: 'media' });
      setJobActive(true);
      if (res.data.already_running) toast.info('A push is already running for this product');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Media push failed');
    } finally {
      setPushingMedia(false);
    }
  };

  // ---------- link an existing (manually-created) Etsy listing ----------
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linking, setLinking] = useState(false);
  const handleLinkListing = async () => {
    const raw = (linkInput || '').trim();
    if (!raw) {
      toast.error('Paste an Etsy listing URL or numeric ID');
      return;
    }
    try {
      setLinking(true);
      const res = await etsyListingAPI.linkListing(product.id, raw);
      toast.success(`Linked to Etsy listing ${res.data.listing_id} — "${(res.data.title || '').slice(0, 40)}"`);
      setLinkDialogOpen(false);
      setLinkInput('');
      // Refresh parent state so the new buttons appear
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not link listing');
    } finally {
      setLinking(false);
    }
  };

  const filteredTaxonomy = useMemo(() => {
    const nodes = defaults?.taxonomy || [];
    const q = taxonomyQuery.trim().toLowerCase();
    if (!q) return nodes.slice(0, 200);
    return nodes.filter((n) => n.path.toLowerCase().includes(q)).slice(0, 200);
  }, [defaults, taxonomyQuery]);

  if (!allowed) return null;

  const isPushing = jobActive && job?.status === 'pending';
  const selectCls = 'w-full h-9 text-sm border border-zinc-200 rounded-md px-2 bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-orange-400';
  const labelCls = 'text-[10px] uppercase tracking-wider font-semibold text-orange-800 mb-1 block';

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="etsy-listing-panel">
      {/* Header + connection */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
            <Store size={15} className="text-orange-600" /> Etsy Draft Publisher
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Push this product to your Etsy shop as a draft — title, description, tags, photos and the full Metal × Carat variation matrix.
          </p>
        </div>
        {status === null ? (
          <Loader2 size={16} className="animate-spin text-zinc-400" />
        ) : status.connected ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="etsy-connected-pill">
              <ShieldCheck size={11} /> Connected{status.shop_name ? ` · ${status.shop_name}` : ''}
            </span>
            <button onClick={handleDisconnect} className="text-zinc-400 hover:text-red-600" title="Disconnect Etsy" data-testid="etsy-disconnect-btn">
              <X size={13} />
            </button>
          </div>
        ) : (
          <Button size="sm" onClick={handleConnect} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="etsy-connect-btn">
            <Link2 size={13} className="mr-1.5" /> Connect Etsy
          </Button>
        )}
      </div>

      {status?.connected && (
        <>
          {/* Shop settings */}
          <div className="bg-orange-50/40 border border-orange-100 rounded-lg p-3 space-y-3" data-testid="etsy-push-settings">
            {loadingDefaults && (
              <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 size={12} className="animate-spin" /> Loading your shop's shipping profiles, sections and categories…</div>
            )}
            {defaults && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Shipping profile *</label>
                  <select value={shippingProfileId} onChange={(e) => setShippingProfileId(e.target.value)} className={selectCls} data-testid="etsy-shipping-select">
                    <option value="">— select —</option>
                    {defaults.shipping_profiles.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Processing profile</label>
                  <select value={readinessStateId} onChange={(e) => setReadinessStateId(e.target.value)} className={selectCls} data-testid="etsy-readiness-select">
                    <option value="">— none —</option>
                    {defaults.readiness_states.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Shop section</label>
                  <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} className={selectCls} data-testid="etsy-section-select">
                    <option value="">— none —</option>
                    {defaults.sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Etsy category *</label>
                  <Input
                    value={taxonomyQuery}
                    onChange={(e) => setTaxonomyQuery(e.target.value)}
                    placeholder="Filter categories…"
                    className="h-7 text-xs mb-1 bg-white"
                    data-testid="etsy-taxonomy-filter"
                  />
                  <select value={taxonomyId} onChange={(e) => setTaxonomyId(e.target.value)} className={selectCls} data-testid="etsy-taxonomy-select">
                    <option value="">— select —</option>
                    {filteredTaxonomy.map((n) => <option key={n.id} value={n.id}>{n.path.replace('Jewelry > ', '')}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Tags + materials */}
          <div className="border border-zinc-200 rounded-lg p-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                Tags ({tags.length}/13)
              </span>
              <Button size="sm" variant="outline" onClick={handleGenerateTags} disabled={generatingTags} className="h-7 text-xs" data-testid="etsy-generate-tags-btn">
                {generatingTags ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Sparkles size={12} className="mr-1.5" />}
                Generate 13 tags
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-[11px] bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5 text-zinc-700" data-testid={`etsy-tag-${t.replace(/\s+/g, '-')}`}>
                  {t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))} className="text-zinc-400 hover:text-red-600"><X size={10} /></button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="+ add tag"
                className="text-[11px] border border-dashed border-zinc-300 rounded-full px-2 py-0.5 w-24 focus:outline-none focus:border-orange-400"
                data-testid="etsy-tag-input"
              />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 block mb-1">Materials</span>
              <Input
                value={materials.join(', ')}
                onChange={(e) => setMaterials(e.target.value.split(',').map((m) => m.trim()).filter(Boolean).slice(0, 13))}
                className="h-8 text-xs"
                data-testid="etsy-materials-input"
              />
            </div>
          </div>

          {/* Etsy structured attributes (auto-generated, editable, pushed with the draft) */}
          {attrDisplay && (attrDisplay.fixed || attrDisplay.derived) && (
            <div className="border border-zinc-200 rounded-lg p-3 space-y-3" data-testid="etsy-attributes-card">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                Etsy attributes <span className="text-zinc-400 normal-case">· auto-set on the draft</span>
              </span>

              {attrDisplay.fixed && Object.keys(attrDisplay.fixed).length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {Object.entries(attrDisplay.fixed).map(([pid, f]) => (
                    <div key={pid} className="text-[11px] text-zinc-600" data-testid={`etsy-attr-fixed-${pid}`}>
                      <span className="font-semibold text-zinc-700">{f.name}:</span>{' '}
                      {(f.values || []).join(', ')}
                    </div>
                  ))}
                </div>
              )}

              {attrDisplay.derived && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(attrDisplay.derived).map(([key, d]) => {
                    const sel = derivedSel[key] || [];
                    const allowed = Array.from(new Set([...(attrDisplay.allowed?.[key] || []), ...sel]));
                    const isSingle = d.max === 1;
                    return (
                      <div key={key} data-testid={`etsy-attr-${key}`}>
                        <label className={labelCls}>{d.name}{isSingle ? '' : ` (max ${d.max})`}</label>
                        {isSingle ? (
                          <select
                            value={sel[0] || ''}
                            onChange={(e) => setDerivedSel((prev) => ({ ...prev, [key]: e.target.value ? [e.target.value] : [] }))}
                            className={selectCls}
                            data-testid={`etsy-attr-select-${key}`}
                          >
                            <option value="">— none —</option>
                            {allowed.map((v) => <option key={v} value={v}>{v}</option>)}
                          </select>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {allowed.map((v) => {
                              const on = sel.includes(v);
                              return (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => toggleMulti(key, v, d.max)}
                                  className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors ${on ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-zinc-600 border-zinc-200 hover:border-orange-300'}`}
                                  data-testid={`etsy-attr-chip-${key}-${v.replace(/\s+/g, '-')}`}
                                >
                                  {v}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Markup + quantity + personalization */}
          <div className="border border-zinc-200 rounded-lg p-3 space-y-3">
            <div className="flex flex-wrap items-end gap-4">
              {!isTennis && (
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 block mb-1">Planned sale discount %</label>
                <Input type="number" min="0" max="80" step="1" value={markupPct} onChange={(e) => setMarkupPct(e.target.value)} className="h-8 w-24 text-sm" data-testid="etsy-markup-input" />
              </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600 block mb-1">Qty / variant</label>
                <Input type="number" min="1" max="999" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="h-8 w-20 text-sm" data-testid="etsy-quantity-input" />
              </div>
              {preview && (
                <div className="text-[11px] text-zinc-600 leading-relaxed" data-testid="etsy-price-preview">
                  <div>Listed: <strong className="font-mono">{fmt(preview.listMin)}–{fmt(preview.listMax)}</strong> · {preview.variantCount} variants</div>
                  {isTennis
                    ? <div className="text-zinc-400">Final prices, no sale markup · IGI adds +${IGI_UPCHARGE}</div>
                    : <div className="text-zinc-400">After {markupPct || 0}% off sale → {fmt(preview.baseMin)}–{fmt(preview.baseMax)} (canonical Etsy price)</div>}
                </div>
              )}
              {isTennis && !preview && (
                <div className="text-[11px] text-amber-700" data-testid="etsy-tennis-prices-missing">Enter the tennis prices in the pricing panel above.</div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-[12px] text-zinc-700">
              {isTennis ? (
                <span className="text-zinc-500" data-testid="etsy-tennis-no-questions">No buyer questions for tennis pieces</span>
              ) : (
                <>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={ringSize} onChange={(e) => setRingSize(e.target.checked)} className="h-3.5 w-3.5 accent-orange-600" data-testid="etsy-ring-size-toggle" />
                    Ring Size (required)
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={personalization} onChange={(e) => setPersonalization(e.target.checked)} className="h-3.5 w-3.5 accent-orange-600" data-testid="etsy-personalization-toggle" />
                    Complimentary Engraving
                  </label>
                </>
              )}
              {hasVideo && (
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={includeVideo} onChange={(e) => setIncludeVideo(e.target.checked)} className="h-3.5 w-3.5 accent-orange-600" data-testid="etsy-include-video-toggle" />
                  Include video
                </label>
              )}
              <span className="text-zinc-400">{photos.length} photo{photos.length === 1 ? '' : 's'} will be uploaded (max 10)</span>
            </div>
            {ringSize && !isTennis && (
              <Input value={ringSizeInstructions} onChange={(e) => setRingSizeInstructions(e.target.value)} className="h-8 text-xs" placeholder="Ring size instructions for the buyer" data-testid="etsy-ring-size-instructions" />
            )}
            {personalization && !isTennis && (
              <Input value={persInstructions} onChange={(e) => setPersInstructions(e.target.value)} className="h-8 text-xs" placeholder="Engraving instructions for the buyer" data-testid="etsy-personalization-instructions" />
            )}
          </div>

          {/* Push + progress + result */}
          <div className="space-y-2.5">
            {isPushing ? (
              <div className="flex items-center gap-2.5 text-sm text-zinc-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2.5" data-testid="etsy-push-progress">
                <Loader2 size={14} className="animate-spin text-orange-600" />
                <span>{job?.step || 'Working…'}</span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={handlePush}
                  disabled={pushing || pushingPricing || pushingMedia || !defaults}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="etsy-push-draft-btn"
                >
                  {pushing
                    ? <Loader2 size={14} className="animate-spin mr-1.5" />
                    : etsyListing?.listing_id ? <RefreshCw size={14} className="mr-1.5" /> : <Rocket size={14} className="mr-1.5" />}
                  {etsyListing?.listing_id ? 'Push update to Etsy draft' : 'Create Etsy draft'}
                </Button>
                {etsyListing?.listing_id && (
                  <>
                    <Button
                      onClick={handlePushPricingOnly}
                      disabled={pushing || pushingPricing || pushingMedia}
                      variant="outline"
                      className="border-orange-200 text-orange-700 hover:bg-orange-50"
                      data-testid="etsy-push-pricing-btn"
                      title="Re-push only the price matrix — no title/description/image changes"
                    >
                      {pushingPricing
                        ? <Loader2 size={14} className="animate-spin mr-1.5" />
                        : <RefreshCw size={14} className="mr-1.5" />}
                      Push pricing only
                    </Button>
                    <div className="flex items-center gap-1.5">
                      <Button
                        onClick={handlePushMediaOnly}
                        disabled={pushing || pushingPricing || pushingMedia}
                        variant="outline"
                        className="border-orange-200 text-orange-700 hover:bg-orange-50"
                        data-testid="etsy-push-media-btn"
                        title="Delete every Etsy image then upload the current photos in order. Title, description, price and tags stay unchanged."
                      >
                        {pushingMedia
                          ? <Loader2 size={14} className="animate-spin mr-1.5" />
                          : <Camera size={14} className="mr-1.5" />}
                        Push media only
                      </Button>
                      <label className="inline-flex items-center gap-1 text-[11px] text-zinc-600 select-none cursor-pointer" title="Also re-upload the video file (replaces Etsy's existing video).">
                        <input
                          type="checkbox"
                          className="accent-orange-600 w-3 h-3"
                          checked={mediaIncludeVideo}
                          onChange={(e) => setMediaIncludeVideo(e.target.checked)}
                          data-testid="etsy-push-media-video-toggle"
                        />
                        video
                      </label>
                    </div>
                  </>
                )}
                {!etsyListing?.listing_id && (
                  <Button
                    onClick={() => setLinkDialogOpen(true)}
                    disabled={pushing || pushingPricing || pushingMedia}
                    variant="outline"
                    className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                    data-testid="etsy-link-existing-btn"
                    title="Already created this listing on Etsy manually? Link it here to enable media/pricing-only pushes."
                  >
                    <Link2 size={14} className="mr-1.5" />
                    Link existing Etsy listing
                  </Button>
                )}
              </div>
            )}

            {/* Link-existing-listing dialog */}
            {linkDialogOpen && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                onClick={() => !linking && setLinkDialogOpen(false)}
                data-testid="etsy-link-dialog-backdrop"
              >
                <div
                  className="bg-white rounded-lg shadow-xl border border-zinc-200 max-w-md w-full p-5"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="etsy-link-dialog"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
                      <Link2 size={16} className="text-orange-600" /> Link existing Etsy listing
                    </h3>
                    <button
                      onClick={() => !linking && setLinkDialogOpen(false)}
                      className="text-zinc-400 hover:text-zinc-700"
                      data-testid="etsy-link-dialog-close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mb-3">
                    Paste the Etsy listing URL <em>or</em> just the numeric ID. We&apos;ll verify the listing exists in your connected shop and link it to this product so you can push media/pricing without recreating the draft.
                  </p>
                  <Input
                    autoFocus
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    placeholder="https://www.etsy.com/listing/1234567890/..."
                    className="text-sm"
                    disabled={linking}
                    data-testid="etsy-link-dialog-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLinkListing(); }}
                  />
                  <div className="flex items-center justify-end gap-2 mt-4">
                    <Button
                      variant="ghost"
                      onClick={() => setLinkDialogOpen(false)}
                      disabled={linking}
                      data-testid="etsy-link-dialog-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleLinkListing}
                      disabled={linking || !linkInput.trim()}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid="etsy-link-dialog-submit"
                    >
                      {linking ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Link2 size={14} className="mr-1.5" />}
                      Link listing
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {job?.status === 'failed' && (
              <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="etsy-push-error">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>{job.error}</span>
              </div>
            )}

            {etsyListing?.listing_id && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 text-sm" data-testid="etsy-listing-result">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-emerald-800">
                    <strong>Draft on Etsy</strong> · listing {etsyListing.listing_id}
                    <span className="text-emerald-600/80 text-xs ml-2">
                      {etsyListing.variant_count} variants · {etsyListing.image_count} photos · {etsyListing.markup_pct}% markup
                    </span>
                  </div>
                  <a
                    href={etsyListing.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 underline"
                    data-testid="etsy-open-listing-link"
                  >
                    Open in Etsy listing editor <ExternalLink size={11} />
                  </a>
                </div>
                {!!(etsyListing.warnings || []).length && (
                  <div className="mt-1.5 text-[11px] text-amber-700">
                    {etsyListing.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
